import {
    Activity,
    Building2,
    Plug,
    Lightbulb,
    Waves,
    Crosshair,
    GraduationCap,
    Zap,
    Wind,
    ArrowUpRight,
} from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useReveal } from "../hooks/useReveal";

const ICONS = {
    Activity,
    Building2,
    Plug,
    Lightbulb,
    Waves,
    Crosshair,
    GraduationCap,
    Zap,
    Wind,
};

function ServiceCard({ item, index }) {
    const { ref, className } = useReveal();
    const Icon = ICONS[item.icon] || Activity;
    return (
        <article
            ref={ref}
            data-testid={`service-card-${item.id}`}
            style={{ transitionDelay: `${(index % 4) * 60}ms` }}
            className={`${className} group relative bg-white border border-border p-6 md:p-7 transition-all duration-300 hover:border-ink hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#7FDDD4] flex flex-col h-full`}
        >
            <div className="flex items-start justify-between mb-5">
                <span className="w-11 h-11 border border-ink flex items-center justify-center bg-white group-hover:bg-mint transition-colors">
                    <Icon size={20} strokeWidth={1.5} className="text-ink" />
                </span>
            </div>

            <h4 className="font-display font-bold text-lg md:text-xl uppercase leading-tight tracking-tight text-ink">
                {item.title}
            </h4>
            <p className="mt-3 text-sm text-ink-soft leading-relaxed flex-1">
                {item.short}
            </p>

            <ul className="mt-5 space-y-2">
                {item.benefits.map((b, i) => (
                    <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-ink"
                    >
                        <span className="mt-2 w-1.5 h-1.5 bg-mint flex-shrink-0" />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>

            <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                <span className="font-display text-xs uppercase tracking-widest text-ink-muted">
                    {item.id}
                </span>
                <ArrowUpRight
                    size={18}
                    className="text-ink group-hover:text-mint-hover group-hover:rotate-45 transition-all duration-300"
                />
            </div>
        </article>
    );
}

function Group({ group, groupIndex }) {
    const head = useReveal();
    const cols =
        group.items.length === 4
            ? "lg:grid-cols-4"
            : group.items.length === 3
            ? "lg:grid-cols-3"
            : "lg:grid-cols-2";
    return (
        <div className="mt-16 first:mt-0">
            <div
                ref={head.ref}
                className={`${head.className} flex items-baseline gap-4 mb-8`}
            >
                <span className="font-display font-bold text-xl md:text-2xl text-mint-hover">
                    0{groupIndex + 1}
                </span>
                <h3
                    data-testid={`service-group-${group.id}`}
                    className="font-display font-bold uppercase text-xl md:text-2xl lg:text-3xl tracking-tight text-ink"
                >
                    {group.title}
                </h3>
                <span className="flex-1 h-px bg-ink ml-4" />
            </div>
            <div
                className={`grid grid-cols-1 sm:grid-cols-2 ${cols} gap-5 md:gap-6`}
            >
                {group.items.map((it, i) => (
                    <ServiceCard key={it.id} item={it} index={i} />
                ))}
            </div>
        </div>
    );
}

export default function Services() {
    const { t } = useLang();
    const { ref, className } = useReveal();
    return (
        <section
            id="services"
            data-testid="services-section"
            className="py-24 md:py-32 bg-paper-alt"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div ref={ref} className={`${className} max-w-3xl mb-12`}>
                    <div className="flex items-center gap-3 mb-6">
                        <span className="w-10 h-px bg-ink" />
                        <span className="font-display text-xs uppercase tracking-[0.3em] text-ink-muted">
                            {t.services.kicker}
                        </span>
                    </div>
                    <h2 className="font-display font-bold uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink">
                        {t.services.title}
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-ink-soft max-w-2xl">
                        {t.services.subtitle}
                    </p>
                </div>

                {t.services.groups.map((g, i) => (
                    <Group key={g.id} group={g} groupIndex={i} />
                ))}
            </div>
        </section>
    );
}
